import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase, Post } from '@/lib/supabase';

const categoryLabels: Record<string, string> = {
  web: 'WEB制作',
  sns: 'SNS運用',
  seo: 'SEO・MEO',
  meo: 'MEO対策',
  marketing: 'マーケティング',
  ai: 'AI活用',
};

const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
  web: { bg: 'rgba(30,111,255,0.1)', text: '#1E6FFF', border: 'rgba(30,111,255,0.25)' },
  sns: { bg: 'rgba(0,153,255,0.1)', text: '#0099FF', border: 'rgba(0,153,255,0.25)' },
  seo: { bg: 'rgba(0,180,120,0.1)', text: '#00B478', border: 'rgba(0,180,120,0.25)' },
  meo: { bg: 'rgba(0,180,120,0.1)', text: '#00B478', border: 'rgba(0,180,120,0.25)' },
  marketing: { bg: 'rgba(255,140,0,0.1)', text: '#FF8C00', border: 'rgba(255,140,0,0.25)' },
  ai: { bg: 'rgba(120,80,255,0.1)', text: '#7850FF', border: 'rgba(120,80,255,0.25)' },
};

function formatDate(post: Post): string {
  const dateStr = post.published_at ?? post.created_at ?? '';
  return dateStr ? dateStr.split('T')[0] : '';
}

export default function HomeBlog() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      const { data } = await supabase
        .from('posts')
        .select('*')
        .eq('published', true)
        .order('published_at', { ascending: false })
        .limit(3);
      setPosts(data ?? []);
      setLoading(false);
    };
    fetchPosts();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.hb-header').forEach((el) => {
              (el as HTMLElement).style.opacity = '1';
              (el as HTMLElement).style.transform = 'translateY(0)';
            });
            entry.target.querySelectorAll('.hb-card').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120 + 200);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [posts]);

  return (
    <>
      <style>{`
        .hb-header {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .hb-card {
          opacity: 0;
          transform: translateY(36px);
          transition: opacity 0.65s ease, transform 0.65s ease;
        }
        .hb-card:hover {
          transform: translateY(-6px) !important;
        }
        .hb-img {
          transition: transform 0.6s ease;
        }
        .hb-card:hover .hb-img {
          transform: scale(1.06);
        }
      `}</style>

      <section className="py-24 md:py-28 bg-[#F8FAFF] relative overflow-hidden" ref={sectionRef}>
        <div
          className="absolute top-0 right-0 w-[32rem] h-[32rem] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(30,111,255,0.05) 0%, transparent 70%)' }}
        />

        <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
          {/* Header */}
          <div className="hb-header mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="text-[#1E6FFF] text-xs tracking-[0.3em] mb-4 uppercase">/ Blog</p>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] tracking-tight">
                ブログ
              </h2>
              <p className="text-[#666] text-sm mt-3 leading-relaxed">
                WEB制作・SNS運用・MEO対策に関する最新情報を発信中
              </p>
            </div>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-[#1E6FFF] font-medium hover:gap-4 transition-all whitespace-nowrap group"
            >
              記事一覧を見る
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          {/* Cards */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-2xl overflow-hidden bg-white animate-pulse" style={{ border: '1px solid rgba(30,111,255,0.1)' }}>
                  <div className="h-48 bg-[#E8F0FF]" />
                  <div className="p-6 space-y-3">
                    <div className="h-3 bg-[#E8F0FF] rounded-full w-1/3" />
                    <div className="h-4 bg-[#E8F0FF] rounded-full w-full" />
                    <div className="h-4 bg-[#E8F0FF] rounded-full w-4/5" />
                    <div className="h-3 bg-[#E8F0FF] rounded-full w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#999] text-sm">記事はまだありません</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {posts.map((post) => {
                const color = categoryColors[post.category] ?? categoryColors.web;
                const label = categoryLabels[post.category] ?? post.category;
                return (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="hb-card block rounded-2xl overflow-hidden bg-white cursor-pointer"
                    style={{ border: '1px solid rgba(30,111,255,0.1)' }}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="hb-img w-full h-full object-cover object-top"
                      />
                      <div className="absolute top-4 left-4">
                        <span
                          className="text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap backdrop-blur-sm"
                          style={{ background: color.bg, color: color.text, border: `1px solid ${color.border}` }}
                        >
                          {label}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-[#999] text-xs">{formatDate(post)}</span>
                        <span className="text-[#ccc]">·</span>
                        <span className="text-[#999] text-xs">{post.reading_time}分で読める</span>
                      </div>
                      <h3 className="text-base font-bold text-[#0A1628] leading-snug mb-3 line-clamp-2 group-hover:text-[#1E6FFF] transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-[#666] text-sm leading-relaxed line-clamp-2 mb-4">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-2 text-[#1E6FFF] text-sm font-semibold">
                        続きを読む
                        <i className="ri-arrow-right-line" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
